import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import ProductCard from "@/components/site/ProductCard";
import { toast } from "sonner";

const CATEGORIES = [
  { title: "Men's Fragrances", subtitle: "Bold, magnetic, unforgettable", to: "/shop?category=mens", match: "Playboy" },
  { title: "Women's Fragrances", subtitle: "Sensual, radiant, elegant", to: "/shop?category=womens", match: "Golden Hour" },
  { title: "Unisex Fragrances", subtitle: "Without boundaries. Purely you.", to: "/shop?category=unisex", match: "After Dark" },
];

function RollonCard({ product, index }) {
  const { addToCart } = useShop();
  const rollon = product.sizes.find((s) => s.label.toLowerCase().includes("roll"));
  if (!rollon) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 5) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group bg-[#0D0D0D] gold-hairline hover:border-[#D4AF37]/60 transition-all duration-500 flex flex-col"
      data-testid={`rollon-card-${product.id}`}
    >
      <Link to={`/product/${product.id}?format=rollon`} className="block aspect-[3/4] overflow-hidden" data-testid={`rollon-link-${product.id}`}>
        {product.rollon_image_url ? (
          <img src={imageUrl(product.rollon_image_url)} alt={`${product.name} roll-on`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.14), #0D0D0D 70%)" }} />
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-serif-display text-lg text-white leading-tight group-hover:text-[#D4AF37] transition-colors">{product.name}</h3>
        <p className="font-sans-body text-[9px] uppercase tracking-[0.25em] text-[#C6A15B] mt-1">Roll-On Oil</p>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <span className="font-mono-price text-[#D4AF37]">${rollon.price.toFixed(2)}</span>
          <button
            onClick={() => addToCart(product, rollon.label, 1)}
            disabled={rollon.stock <= 0}
            className="bg-[#D4AF37] text-[#080808] font-sans-body text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-2 hover:bg-[#F1DC9A] transition-colors disabled:opacity-40"
            data-testid={`rollon-add-${product.id}`}
          >
            Add
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const { settings } = useShop();
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 220]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0.25]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/products").then((r) => {
      setAllProducts(r.data);
      const sorted = [...r.data].sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0));
      setBestsellers(sorted.slice(0, 3));
    }).catch(() => {});
  }, []);

  const imageByName = (name) => allProducts.find((p) => p.name === name)?.image_url || "";

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Welcome to the private list");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    }
    setSubscribing(false);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden grain" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: heroY, opacity: heroOpacity }}>
          {settings?.hero_image_url && (
            <img
              src={imageUrl(settings.hero_image_url)}
              alt="Kubwalo Fragrances signature bottle"
              className="w-full h-full object-cover object-center"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/55 to-[#080808]/85" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.14), transparent 60%)" }} />
        </motion.div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-20">
          <motion.p
            className="eyebrow mb-8"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
            data-testid="hero-eyebrow"
          >
            An Independent Fragrance House
          </motion.p>
          <h1 className="font-serif-display font-light text-white leading-[1.04] tracking-tight text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-headline">
            <MaskedLines lines={["LEAVE A", "LASTING", "IMPRESSION."]} baseDelay={0.35} />
          </h1>
          <motion.p
            className="mt-8 font-sans-body text-base sm:text-lg text-[#B8B8B8] font-light max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.9 }}
            data-testid="hero-subtext"
          >
            Discover your signature scent. Explore fragrances crafted for unforgettable
            moments, undeniable confidence, and individual expression.
          </motion.p>
          <motion.div
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.15 }}
          >
            <Link to="/shop" className="btn-gold" data-testid="hero-shop-collection-button">
              Shop the Collection <ArrowRight size={15} />
            </Link>
            <Link to="/about" className="btn-outline-gold" data-testid="hero-discover-button">
              Discover Kubwalo
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
        >
          <span className="font-sans-body text-[10px] uppercase tracking-[0.4em] text-[#C6A15B]/70">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/60 to-transparent" />
        </motion.div>
      </section>

      <Marquee />

      {/* SIGNATURE COLLECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32" data-testid="signature-collection">
        <Reveal>
          <p className="eyebrow text-center mb-4">Curated Elegance</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white text-center font-light" data-testid="signature-title">
            The Signature Collection
          </h2>
          <div className="w-16 h-px bg-[#D4AF37]/50 mx-auto mt-8" />
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-16">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        <Reveal className="text-center mt-14">
          <Link to="/shop" className="btn-outline-gold" data-testid="signature-view-all-button">
            View All Fragrances <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>

      {/* ROLL-ON COLLECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32" data-testid="rollon-collection">
        <Reveal>
          <p className="eyebrow text-center mb-4">Same Scent. New Ritual.</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white text-center font-light" data-testid="rollon-title">
            The Roll-On Collection — $10
          </h2>
          <p className="mt-5 text-center font-sans-body text-sm text-[#B8B8B8] font-light max-w-lg mx-auto">
            Every Kubwalo fragrance in a travel-ready clear-glass roll-on with a black cap.
            Pocket-sized, precise, and always with you.
          </p>
          <div className="w-16 h-px bg-[#D4AF37]/50 mx-auto mt-8" />
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 lg:gap-6 mt-14">
          {allProducts
            .filter((p) => p.sizes.some((s) => s.label.toLowerCase().includes("roll") && s.stock > 0))
            .slice(0, 10)
            .map((p, i) => <RollonCard key={p.id} product={p} index={i} />)}
        </div>
        <Reveal className="text-center mt-12">
          <Link to="/shop?format=rollon" className="btn-outline-gold" data-testid="rollon-view-all-button">
            View All Roll-Ons <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>

      {/* BESTSELLERS — asymmetric bento */}
      <section className="bg-[#0A0A0A] gold-hairline-t gold-hairline-b" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
          <Reveal>
            <p className="eyebrow mb-4">Most Coveted</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white font-light max-w-2xl leading-tight" data-testid="bestsellers-title">
              The Scents Everyone Is Talking About
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 mt-16">
            {bestsellers[0] && (
              <motion.div
                className="md:col-span-7"
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9 }}
              >
                <Link to={`/product/${bestsellers[0].id}`} className="group block relative aspect-[4/3] overflow-hidden gold-hairline" data-testid={`bestseller-hero-${bestsellers[0].id}`}>
                  {bestsellers[0].image_url ? (
                    <img src={imageUrl(bestsellers[0].image_url)} alt={bestsellers[0].name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full bg-[#0D0D0D] transition-transform duration-700 group-hover:scale-105">
                      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.16), transparent 65%)" }} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <span className="bg-[#D4AF37] text-[#080808] font-sans-body text-[9px] font-bold uppercase tracking-[0.2em] px-2.5 py-1">No. 1 Bestseller</span>
                    <h3 className="font-serif-display text-3xl sm:text-4xl text-white mt-4">{bestsellers[0].name}</h3>
                    <p className="font-mono-price text-[#D4AF37] mt-2">${Math.min(...bestsellers[0].sizes.map((s) => s.price)).toFixed(2)}</p>
                  </div>
                </Link>
              </motion.div>
            )}
            <div className="md:col-span-5 grid grid-rows-2 gap-6 lg:gap-8">
              {bestsellers.slice(1, 3).map((p, i) => (
                <motion.div key={p.id}
                  initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay: 0.1 * (i + 1) }}
                >
                  <Link to={`/product/${p.id}`} className="group relative flex items-stretch gold-hairline bg-[#0D0D0D] overflow-hidden h-full hover:border-[#D4AF37]/60 transition-colors" data-testid={`bestseller-side-${p.id}`}>
                    <div className="w-32 sm:w-40 shrink-0 overflow-hidden">
                      {p.image_url ? (
                        <img src={imageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.14), #0D0D0D 70%)" }} />
                      )}
                    </div>
                    <div className="p-6 flex flex-col justify-center">
                      <p className="font-sans-body text-[10px] uppercase tracking-[0.3em] text-[#C6A15B]">{p.category}</p>
                      <h3 className="font-serif-display text-2xl text-white mt-1 group-hover:text-[#D4AF37] transition-colors">{p.name}</h3>
                      <p className="font-mono-price text-[#D4AF37] text-sm mt-2">${Math.min(...p.sizes.map((s) => s.price)).toFixed(2)}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="relative py-32 sm:py-44 overflow-hidden grain" data-testid="brand-statement">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(212,175,55,0.12), transparent 55%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <span className="font-serif-display italic text-[#D4AF37]/70 text-7xl leading-none">&ldquo;</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-tight -mt-6" data-testid="brand-statement-title">
              More than a fragrance. <span className="gold-text">It's your signature.</span>
            </h2>
            <p className="mt-8 font-sans-body text-base sm:text-lg text-[#B8B8B8] font-light leading-relaxed max-w-2xl mx-auto">
              At Kubwalo Fragrances, we believe fragrance is an extension of your identity.
              Every scent tells a story, creates a memory, and leaves an impression long
              after you've left the room.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32" data-testid="categories-section">
        <Reveal>
          <p className="eyebrow text-center mb-4">Find Your Expression</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white text-center font-light">
            Explore the Collections
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-16">
          {CATEGORIES.map((c, i) => {
            const catImg = imageByName(c.match) || settings?.hero_image_url || "";
            return (
            <Reveal key={c.title} delay={i * 0.12}>
              <Link to={c.to} className="group relative block aspect-[3/4] overflow-hidden gold-hairline hover:border-[#D4AF37]/60 transition-colors duration-500" data-testid={`category-card-${c.title.split("'")[0].toLowerCase()}`}>
                {catImg ? (
                  <img src={imageUrl(catImg)} alt={c.title} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" loading="lazy" />
                ) : (
                  <div className="w-full h-full bg-[#0D0D0D]" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.14), #0D0D0D 70%)" }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20" />
                <div className="absolute bottom-0 inset-x-0 p-8">
                  <h3 className="font-serif-display text-2xl sm:text-3xl text-white">{c.title}</h3>
                  <p className="font-sans-body text-xs uppercase tracking-[0.25em] text-[#C6A15B] mt-2">{c.subtitle}</p>
                  <span className="inline-flex items-center gap-2 mt-5 font-sans-body text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] group-hover:gap-3 transition-all">
                    Explore <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </Reveal>
            );
          })}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#0A0A0A] gold-hairline-t relative grain" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center">
          <Reveal>
            <p className="eyebrow mb-4">The Private List</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white font-light" data-testid="newsletter-title">
              Be the First to Know.
            </h2>
            <p className="mt-6 font-sans-body text-base text-[#B8B8B8] font-light max-w-lg mx-auto">
              Subscribe for new fragrance releases, exclusive offers, and brand updates.
            </p>
            <form onSubmit={subscribe} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-transparent border border-[#D4AF37]/30 px-5 py-4 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] transition-colors"
                data-testid="newsletter-email-input"
              />
              <button type="submit" className="btn-gold whitespace-nowrap" disabled={subscribing} data-testid="newsletter-subscribe-button">
                {subscribing ? "Joining..." : "Join the List"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
